// This file is auto-generated by @hey-api/openapi-ts

import { type DefaultError, queryOptions, type UseMutationOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';

import { client } from '../client.gen';
import { getFinancialsTable, type Options, patchFinancialsTable } from '../sdk.gen';
import type { GetFinancialsTableData, GetFinancialsTableResponse, PatchFinancialsTableData, PatchFinancialsTableResponse } from '../types.gen';

export type QueryKey<TOptions extends Options> = [
    Pick<TOptions, 'baseURL' | 'body' | 'headers' | 'path' | 'query'> & {
        _id: string;
        _infinite?: boolean;
        tags?: ReadonlyArray<string>;
    }
];

const createQueryKey = <TOptions extends Options>(id: string, options?: TOptions, infinite?: boolean, tags?: ReadonlyArray<string>): [
    QueryKey<TOptions>[0]
] => {
    const params: QueryKey<TOptions>[0] = { _id: id, baseURL: options?.baseURL || (options?.client ?? client).getConfig().baseURL } as QueryKey<TOptions>[0];
    if (infinite) {
        params._infinite = infinite;
    }
    if (tags) {
        params.tags = tags;
    }
    if (options?.body) {
        params.body = options.body;
    }
    if (options?.headers) {
        params.headers = options.headers;
    }
    if (options?.path) {
        params.path = options.path;
    }
    if (options?.query) {
        params.query = options.query;
    }
    return [params];
};

export const getFinancialsTableQueryKey = (options: Options<GetFinancialsTableData>) => createQueryKey('getFinancialsTable', options, false, ['centro-portal', 'financials-table']);

/**
 * Get the Financials table for an RFP
 *
 * Returns the denormalized payload that powers the
 * Financials sub-tab of the *Present* tab.
 *
 * The response contains the following top-level collections:
 * - `coverages` — one entry per requested coverage, in
 * display order (`order`).
 * - `periods` — fixed object with `current`, `renewal`, and
 * `revisedRenewal` slots. Each slot owns its per-coverage
 * annual premium values plus a `hidden` flag.
 * - `carriers` — one entry per carrier the RFP was sent to,
 * in display order. Each carrier owns its per-coverage
 * annual premium and (optional) per-cell note.
 * - `view` — per-axis indication of where the returned
 * `coverages` / `carriers` ordering and visibility came
 * from (page-local vs. broker-global).
 * - `brokerConfig` — broker-scoped display preferences
 * (e.g., whether the dollar-savings rows should render).
 * - `tablePreferences` — opaque AG Grid presentation state
 * (column widths, etc.) the FE round-trips through MPX's
 * existing grid-state mechanism.
 *
 * Per-cell values are stored on the **owning column**: a
 * period column owns its `annualPremium` per coverage; a
 * carrier column owns its `annualPremium` and optional
 * `note` per coverage. The `coverages` collection is
 * metadata only.
 *
 * Returns `200` with empty `coverages` / `carriers` arrays
 * only when the RFP exists but has no requested coverages
 * and no invited carriers. Otherwise the populated arrays
 * describe the grid the FE should render.
 *
 */
export const getFinancialsTableOptions = (options: Options<GetFinancialsTableData>) => queryOptions<GetFinancialsTableResponse, AxiosError<DefaultError>, GetFinancialsTableResponse, ReturnType<typeof getFinancialsTableQueryKey>>({
    queryFn: async ({ queryKey, signal }) => {
        const { data } = await getFinancialsTable({
            ...options,
            ...queryKey[0],
            signal,
            throwOnError: true
        });
        return data;
    },
    queryKey: getFinancialsTableQueryKey(options)
});

/**
 * Apply a sparse update to the Financials table for an RFP
 *
 * Sparse update that mirrors the shape of
 * `GetFinancialsTableResponse`. Every top-level key is
 * optional, every nested key is optional.
 *
 * Sparse semantics:
 * - **Omit a key** to leave its current value untouched.
 * - **Send `null`** for an editable scalar to clear it
 * (e.g., `annualPremium: null` to remove a quoted
 * premium; `note: null` to delete a note).
 * - **Send a value** to set/replace it. For `order` and
 * `hidden`, this is an absolute set, not a delta.
 *
 * Page-local vs global writes:
 * - The `view.coveragesApplyTo` / `view.carriersApplyTo`
 * fields are only meaningful when the same PATCH also
 * includes the corresponding `coverages` / `carriers`
 * payload. The BFF rejects a PATCH that sets one without
 * the other (HTTP `400`).
 * - Ordinary edits (typical autosave flushes) omit `view`
 * entirely and are treated as page-local.
 *
 * Validation rules enforced server-side:
 * - `annualPremium` is a non-negative integer (whole
 * dollars) when set; `null` is allowed to clear.
 * - `note` is plain text up to 1024 characters when set;
 * `null` is allowed to clear.
 * - `order` values within an axis must be unique
 * non-negative integers and form a permutation of the
 * existing entries on that axis (no inserts or deletes
 * via PATCH).
 * - Unknown coverage / carrier IDs are rejected.
 *
 * Returns `204 No Content` on success — the FE refetches
 * via React Query when it needs the updated cache.
 *
 */
export const patchFinancialsTableMutation = (options?: Partial<Options<PatchFinancialsTableData>>): UseMutationOptions<PatchFinancialsTableResponse, AxiosError<DefaultError>, Options<PatchFinancialsTableData>> => {
    const mutationOptions: UseMutationOptions<PatchFinancialsTableResponse, AxiosError<DefaultError>, Options<PatchFinancialsTableData>> = {
        mutationFn: async (fnOptions) => {
            const { data } = await patchFinancialsTable({
                ...options,
                ...fnOptions,
                throwOnError: true
            });
            return data;
        }
    };
    return mutationOptions;
};
